import React, { useState, useEffect } from 'react';
import { IoChevronBackSharp, IoChevronForwardSharp } from "react-icons/io5"
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { updateApplicationData, safePdf, isThunkFulfilled } from '../../../../store/reducers/applicationSlice';
import { showSuccess, showError } from '../../../../helpers/toast';
import { createNotification } from "../../../../store/reducers/notificationSlice";
import { prepareHtmlForPdf } from '../../../../utility/utility';
import ConfirmationModal from '../../../common/confirmationModal';
import { AUDIT_TRAIL_ENABLED } from '../../../../helpers/features';

const FormHeader = (props) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadingIds, setDownloadingIds] = useState([]);
    const [inputFields, setInputFields] = useState({});
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const user = useSelector((state) => state.auth.user);
    const authToken = user.access_token;
    const application = useSelector((state) => state.applications.application);
    const pageIds = props.pageIds || [];

    useEffect(() => {
        setInputFields(application?.json_data || {});
    }, [application]);

    const handleGoPrev = (event) => {
        props.previousPage(event);
    }

    const handleGoForw = (event) => {
        props.nextPage(event);
    }

    const handleDownload = async (item) => {
        const applicationId = item.applicationId || props.applicationId;
        const userId = item.userId || props.userId || user.user?.id;
        if (!applicationId) {
            showError('Application ID is missing');
            return;
        }
        if (!userId) {
            showError('User ID is missing');
            return;
        }

        if (isDownloading || downloadingIds.includes(applicationId)) {
            return;
        }

        setDownloadingIds((prev) => [...prev, applicationId]);
        setIsDownloading(true);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}file/${userId}/downloadapplicationfiles/${applicationId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/zip",
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Download failed: ${response.status} ${response.statusText}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `application_${applicationId}_files.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            showSuccess('Files downloaded successfully!');

        } catch (error) {
            showError(`Download failed: ${error.message}`);
        } finally {
            setDownloadingIds((prev) => prev.filter((id) => id !== applicationId));
            setIsDownloading(false);
        }
    };

    const handleSaveApplication = async () => {
        let htmlForm = document.getElementById("DocumentPDF").outerHTML;

        try {
            if (props.saveChanges) {
                await props.saveChanges();
            }

            const response = await dispatch(updateApplicationData({
                id: application.id,
                token: authToken,
                userId: application.user_id,
                json_data: props?.json_data,
                state: application.state,
            }));

            if (isThunkFulfilled(response)) {
                dispatch(safePdf({
                    htmlForm: prepareHtmlForPdf(htmlForm, props?.json_data || {}, pageIds),
                    user: { id: application.user_id },
                    id: params.id,
                    token: authToken,
                }));
            } else {
                return;
            }
        } catch (error) {
            showError('Error saving application. Please try again.');
        }
    };

    const executeSubmitToGB = async (remarks) => {
        let htmlForm = document.getElementById("DocumentPDF")?.outerHTML;
        const currentAppState = application?.state;
        const newState = currentAppState === "descrepency_GB_Company" ? "descrepency_GB_GB" : "in_process";

        try {
            if (props.saveChanges) {
                await props.saveChanges();
            }
            const payload = {
                id: props.applicationId,
                form: props.applicationName,
                state: newState,
                userId: user.user.id,
                token: authToken,
                json_data: props?.json_data,
            };
            if (remarks) payload.remarks = remarks;

            const response = await dispatch(updateApplicationData(payload));

            if (!isThunkFulfilled(response)) {
                return;
            }

            dispatch(createNotification({
                user_id: application?.user_id || application?.userId || props.userId,
                applicationId: props.applicationId,
                applicationName: props.applicationName,
                status: newState,
                accessToken: authToken,
                sendToGamingBoardAdmins: true,
                companyName: user?.user?.company_name,
                employeeName: application?.userName || application?.name
            }));
            showSuccess('Application submitted to Gaming Board successfully!');
            navigate(
                newState === "descrepency_GB_GB" ? "/discrepancies-gb" : "/in-process-applications"
            );
            if (props.refreshApplications) {
                props.refreshApplications();
            }
            if (htmlForm) {
                dispatch(safePdf({
                    htmlForm: prepareHtmlForPdf(htmlForm, props?.json_data || {}, pageIds),
                    user: { id: application.user_id },
                    id: params.id,
                    token: authToken,
                }));
            }
        } catch (error) {
            showError('Failed to submit application to Gaming Board');
        }
    };

    const executeAssignToEmployee = async (remarks) => {
        try {
            const newState = application.state === "descrepency_GB_Company" ? "descrepency_GB_Employee" : "dicrepancy_application_assigned_to_comapany_user";
            const payload = {
                id: props.applicationId,
                form: props.applicationName,
                state: newState,
                userId: user.user.id,
                token: authToken,
                json_data: props?.json_data
            };
            if (remarks) payload.remarks = remarks;

            await dispatch(updateApplicationData(payload)).unwrap();

            dispatch(createNotification({
                user_id: application?.user_id || application?.userId || props.userId,
                applicationId: props.applicationId,
                applicationName: props.applicationName,
                status: newState,
                accessToken: authToken,
                sendToCompanyAdmins: true,
                sendToCompanyUser: true,
                companyName: user?.user?.company_name,
                employeeName: application?.userName || application?.name
            }));

            navigate('/discrepancies-company');
        }
        catch (error) {
            showError('Failed to assign application to employee');
        }
    };

    const executeSaveDraft = async (remarks) => {
        try {
            const payload = {
                id: props.applicationId,
                form: props.applicationName,
                state: "drafts",
                userId: user.user.id,
                token: authToken,
                json_data: props?.json_data,
            };
            if (remarks) payload.remarks = remarks;

            await dispatch(updateApplicationData(payload)).unwrap();
            navigate('/draft');
        }
        catch (error) {
            showError('Failed to save application to drafts');
        }
    };

    const executeAssignToDiscrepancies = async (remarks) => {
        let htmlForm = document.getElementById("DocumentPDF")?.outerHTML;

        try {
            const payload = {
                id: props.applicationId,
                form: props.applicationName,
                userId: user.user.id,
                state: "application_sent_to_discrepancy_by_company",
                token: authToken,
                json_data: props?.json_data
            };
            if (remarks) payload.remarks = remarks;

            const response = await dispatch(updateApplicationData(payload));

            if (!isThunkFulfilled(response)) {
                return;
            }

            showSuccess('Application assigned to employee successfully!');
            const navId = props.applicationId;
            if (navId) {
                navigate(`/view-discrepancies/company/${navId}`);
            }
            if (props.refreshApplications) {
                props.refreshApplications();
            }
            if (htmlForm) {
                dispatch(safePdf({
                    htmlForm: prepareHtmlForPdf(htmlForm, props?.json_data || {}, pageIds),
                    user: { id: application.user_id },
                    id: params.id,
                    token: authToken,
                }));
            }
        } catch (error) {
            showError('Failed to assign application to employee');
        }
    };

    const confirmConfigs = {
        submitToGB: {
            title: "Submit to Gaming Board",
            description: "Optionally add remarks for the audit trail, then confirm submission.",
            confirmLabel: "Submit",
            execute: executeSubmitToGB,
        },
        assignDiscrepancies: {
            title: "Add to Discrepancies",
            description: "Optionally add remarks for the audit trail.",
            confirmLabel: "Confirm",
            execute: executeAssignToDiscrepancies,
        },
        assignEmployee: {
            title: "Assign to Employee",
            description: "Optionally add remarks for the audit trail.",
            confirmLabel: "Assign",
            execute: executeAssignToEmployee,
        },
        saveDraft: {
            title: "Save as Draft",
            description: "Optionally add remarks for the audit trail.",
            confirmLabel: "Save Draft",
            execute: executeSaveDraft,
        },
    };

    const handleConfirm = async (remarks) => {
        const config = confirmConfigs[confirmAction];
        if (!config) return;
        setConfirmLoading(true);
        try {
            await config.execute(remarks);
            setConfirmAction(null);
        } finally {
            setConfirmLoading(false);
        }
    };

    const activeConfirm = confirmAction ? confirmConfigs[confirmAction] : null;

    return (
        <>
            <div className='AppFormBar noShadow'>
                <div className='title'>
                    <div style={{
                        background: "#323639",
                        border: "solid 0px #ccc",
                        borderRadius: "5px",
                        padding: "5px",
                        fontSize: "14px",
                        lineHeight: "22px",
                        color: "#fff",
                    }}>
                        {props.applicationName}
                    </div>
                </div>

                <div className='pagination'>
                    <div className='pageNum d-flex align-items-center'>
                        <IoChevronBackSharp onClick={handleGoPrev} />

                        <input
                            type="number"
                            value={props.currentPage || 1}
                            onChange={props.handlePageChange ? (e) => props.handlePageChange(Number(e.target.value)) : undefined}
                            onFocus={(e) => e.target.select()}
                            onClick={(e) => e.target.select()}
                            readOnly={!props.handlePageChange}
                            style={{
                                width: 'max-content',
                                padding: '0',
                                background: '#191B1C',
                                color: 'white',
                                borderRadius: '5px',
                                textAlign: 'center',
                                WebkitAppearance: 'none',
                                border: 'none',
                            }}
                            min="1"
                            max={Number(props.totalPages)}
                        />

                        <span className='slash'>{`/`}<span style={{ marginLeft: 10 }}></span></span>{props.totalPages}
                        <IoChevronForwardSharp onClick={handleGoForw} />
                    </div>
                    {!props.displayInReview && (
                        <div className='d-flex gap-2'>
                            <button className='btn draft' type='submit' onClick={(() => props.saveToDrafts("drafts"))}>Save to Draft</button>
                            <button className='btn' type='submit' onClick={(props.handleSubmitWithLoading)}>Submit </button>
                        </div>
                    )}
                    {props.displayInReview && (
                        <div className="boardActions d-flex align-items-center gap-2">
                            {AUDIT_TRAIL_ENABLED && props.onOpenAuditTrail && (
                                <button
                                    type="button"
                                    className="btn btn-outline-light btn-sm"
                                    onClick={props.onOpenAuditTrail}
                                    style={{ whiteSpace: "nowrap" }}
                                >
                                    Audit Trail
                                </button>
                            )}
                            <div className="dropdown d-inline-flex">

                                <button
                                    className="btn dropdown-toggle "
                                    type="button"
                                    id="dropdownMenuButton"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    View Actions
                                </button>

                                <ul
                                    className="dropdown-menu p-2"
                                    aria-labelledby="dropdownMenuButton"
                                >
                                    <li>
                                        <button
                                            onClick={() => {
                                                if (!isDownloading && !downloadingIds.includes(props.applicationId || application?.id)) {
                                                    handleDownload(props);
                                                }
                                            }}
                                            className="dropdown-item"
                                            style={{
                                                opacity: isDownloading || downloadingIds.includes(props.applicationId || application?.id) ? 0.6 : 1,
                                                pointerEvents: isDownloading || downloadingIds.includes(props.applicationId || application?.id) ? 'none' : 'auto',
                                                cursor: isDownloading || downloadingIds.includes(props.applicationId || application?.id) ? 'not-allowed' : 'pointer',
                                                background: 'none',
                                                border: 'none',
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '8px 16px'
                                            }}
                                            disabled={isDownloading || downloadingIds.includes(props.applicationId || application?.id)}
                                        >
                                            {isDownloading || downloadingIds.includes(props.applicationId || application?.id) ? 'Downloading...' : 'Download'}
                                        </button>
                                    </li>
                                    {!props.viewOnly && props.applicationState === "application_submitted_by_company_user_to_company" && (
                                        <>
                                            <li>
                                                <button
                                                    type="button"
                                                    className="dropdown-item"
                                                    style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', padding: '8px 16px' }}
                                                    onClick={() => setConfirmAction("submitToGB")}
                                                >
                                                    Submit to GB
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    type="button"
                                                    onClick={() => setConfirmAction("assignDiscrepancies")}
                                                    className="dropdown-item"
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        width: '100%',
                                                        textAlign: 'left',
                                                        padding: '8px 16px'
                                                    }}
                                                >
                                                    Add to Discrepancies
                                                </button>
                                            </li>
                                        </>
                                    )}
                                    {!props.viewOnly && (
                                    <li>
                                        <button
                                            onClick={handleSaveApplication}
                                            className="dropdown-item"
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '8px 16px'
                                            }}
                                        >
                                            Save Application
                                        </button>
                                    </li>
                                    )}
                                    {!props.viewOnly && props.userRole === "company_admin" && (
                                        <>
                                            <li>
                                                <button
                                                    type="button"
                                                    className="dropdown-item"
                                                    style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', padding: '8px 16px' }}
                                                    onClick={() => setConfirmAction("submitToGB")}
                                                >
                                                    Submit to GB
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    type="button"
                                                    className="dropdown-item"
                                                    style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', padding: '8px 16px' }}
                                                    onClick={() => setConfirmAction("assignEmployee")}
                                                >
                                                    Assign to Employee
                                                </button>
                                            </li>
                                        </>
                                    )}

                                    {!props.viewOnly && props.userRole === "employee" && props.applicationState === "application_submitted_by_company_user_to_company" && (
                                        <li>
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', padding: '8px 16px' }}
                                                onClick={() => setConfirmAction("saveDraft")}
                                            >
                                                Save as Draft
                                            </button>
                                        </li>
                                    )}
                                    {!props.viewOnly && props.userRole === "employee" && (
                                        <li>
                                            <Link onClick={() => props.handleSubmitWithLoading && props.handleSubmitWithLoading()} className="dropdown-item">
                                                Assign to Company
                                            </Link>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {activeConfirm && (
                <ConfirmationModal
                    show={!!confirmAction}
                    handleClose={() => !confirmLoading && setConfirmAction(null)}
                    title={activeConfirm.title}
                    description={activeConfirm.description}
                    confirmLabel={activeConfirm.confirmLabel}
                    onConfirm={handleConfirm}
                    showRemarks
                    loading={confirmLoading}
                />
            )}
        </>
    )
}

export default FormHeader;
